/**
 * Planning Tasks Query Hooks (Epic C-1 — Fundament, Story C-1.3)
 *
 * Eigen bestand, zelfde query-keys-factory + invalidatie-patroon als
 * `useCoreTasks.ts` (AD-9/AD-18).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { planningApi } from '../../../lib/planningApi';
import type { CreatePlanningTaskInput, UpdatePlanningTaskInput } from '@functieloonhuis/shared';

export const planningTaskKeys = {
  all: ['planningTasks'] as const,
  lists: () => [...planningTaskKeys.all, 'list'] as const,
  list: (includeArchived: boolean) => [...planningTaskKeys.lists(), { includeArchived }] as const,
};

export function usePlanningTasks(includeArchived = false) {
  return useQuery({
    queryKey: planningTaskKeys.list(includeArchived),
    queryFn: () => planningApi.listTasks(includeArchived),
  });
}

export function useCreateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePlanningTaskInput) => planningApi.createTask(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planningTaskKeys.lists() });
    },
  });
}

export function useUpdateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdatePlanningTaskInput }) => planningApi.updateTask(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planningTaskKeys.lists() });
    },
  });
}

export function useArchiveTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => planningApi.archiveTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planningTaskKeys.lists() });
    },
  });
}

export function useRestoreTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => planningApi.restoreTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planningTaskKeys.lists() });
    },
  });
}
